import React, { useState, useEffect, useRef } from "react";
import { useField, useFormikContext } from "formik";
import * as AutosuggestComponent from "react-autosuggest";
// import _throttle from 'lodash.throttle';
import { Flex, Spinner } from "@chakra-ui/react";

import { AutosuggestStyled, Suggestion } from "./styled";
import { autoSuggestTheme } from "./autoSuggestTheme";
import LabelForm from "components/RFQ/LabelForm/LabelForm";

// Autosuggest will call this function every time you need to clear suggestions.

export const Autosuggest = ({
	name,
	required,
	label,
	className,
	queryFunction,
	handleClick,
	customInputProps,
	searchResults,
	...props
}) => {
	// eslint-disable-next-line
	const [suggestions, setSuggestions] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const loadingTimeoutRef = useRef(null);

	//eslint-disable-next-line
	const [field, meta] = useField(name);
	const { setFieldValue } = useFormikContext();

	const onSuggestionsFetchRequested = (search) => {
		if (queryFunction && search.reason === "input-changed") {
			setIsLoading(true);

			// lear any existing timeout
			if (loadingTimeoutRef.current) {
				clearTimeout(loadingTimeoutRef.current);
			}

			queryFunction(search.value);
		}
	};

	//on suggestion click
	const getSuggestionValue = (suggestion) => suggestion.name;

	//on suggestions clear
	const onSuggestionsClearRequested = () => {
		setSuggestions([]);
		// don't immediately clear loading state on suggestions clear
		// Let the searchResults update handle that
	};

	//render suggestion
	const renderSuggestion = (suggestion) => (
		<Suggestion
			onClick={() => {
				if (handleClick) {
					handleClick(suggestion);
				} else {
					console.log("No click handler passed");
				}
			}}
		>
			{suggestion.name}
		</Suggestion>
	);

	const onChange = (event, { newValue }) => {
		setFieldValue(name, newValue);
	};

	const inputProps = {
		value: field.value,
		onChange: onChange,
	};

	// When search results are updated, turn off loading state
	useEffect(() => {
		// if searchResults exist and it's an array, set a timeout to turn off loading
		// ensures the loading state stays visible a bit longer
		if (searchResults && Array.isArray(searchResults)) {
			// set a minimum display time for the loading indicator
			loadingTimeoutRef.current = setTimeout(() => {
				setIsLoading(false);
			}, 600);
		}

		// cleanup the timeout on unmount
		return () => {
			if (loadingTimeoutRef.current) {
				clearTimeout(loadingTimeoutRef.current);
			}
		};
	}, [searchResults]);

	return (
		<AutosuggestStyled className={className}>
			{label && (
				<LabelForm
					label={label}
					required={required}
				></LabelForm>
			)}
			<div className="relative">
				<AutosuggestComponent
					suggestions={
						searchResults && Array.isArray(searchResults) ? searchResults : []
					}
					onSuggestionsFetchRequested={onSuggestionsFetchRequested}
					onSuggestionsClearRequested={onSuggestionsClearRequested}
					getSuggestionValue={getSuggestionValue}
					renderSuggestion={renderSuggestion}
					inputProps={customInputProps ? customInputProps : inputProps}
					theme={autoSuggestTheme}
				/>
				{isLoading && field.value && field.value.length > 0 && (
					<Flex
						className="absolute right-3 top-1/2 z-20 -translate-y-1/2"
						data-testid="loading-indicator"
						style={{ pointerEvents: "none" }}
					>
						<Spinner
							size="sm"
							color="#0045F5"
							speed="0.8s"
							thickness="2px"
						/>
					</Flex>
				)}
			</div>
		</AutosuggestStyled>
	);
};
